import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSubscription } from "@/lib/subscription";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [polls, setPolls] = useState(0);
  const sub = useSubscription();
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus(r.data);
        if (r.data.payment_status === "paid" || r.data.payment_status === "trialing") {
          sub.refresh?.();
          return;
        }
        if (polls > 15) return;
        setPolls(p => p + 1);
        setTimeout(tick, 2000);
      } catch { setTimeout(tick, 2500); }
    };
    tick();
    return () => { cancelled = true; };
     
  }, [sessionId]);

  const done = status.payment_status === "paid" || status.payment_status === "trialing";
  const trialing = status.payment_status === "trialing";
  const trialDays = status.trial_days || 7;
  return (
    <div className="min-h-screen bg-[#14100c] text-[#f4ede2] flex items-center justify-center p-8">
      <div className="max-w-md w-full text-center space-y-6" data-testid="payment-success-page">
        {done ? (
          <>
            <div className="w-16 h-16 rounded-full bg-[#241d16] border border-[#34d399] flex items-center justify-center mx-auto">
              <CheckCircle2 size={32} className="text-[#34d399]" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#34d399] font-semibold">
              {trialing ? "Free Trial Started" : "Payment Successful"}
            </div>
            <h1 className="font-display text-4xl font-bold">Welcome to The Close Out.</h1>
            <p className="text-[14px] text-[#b8ac96]">
              {trialing
                ? `Your ${trialDays}-day free trial is active. All premium features unlocked. You will not be charged until day ${trialDays + 1}. Cancel anytime in Settings.`
                : "Your subscription is active. All premium features are unlocked."}
            </p>
            <Link to="/app/dashboard" data-testid="post-payment-cta" className="inline-block px-6 py-3 rounded bg-[#d4a574] text-[#14100c] font-semibold">Go to Dashboard →</Link>
          </>
        ) : (
          <>
            <div className="w-16 h-16 rounded-full bg-[#241d16] border border-[#d4a574] flex items-center justify-center mx-auto">
              <Loader2 size={28} className="text-[#d4a574] animate-spin" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] font-semibold">Confirming Payment</div>
            <h1 className="font-display text-3xl font-bold">Almost there…</h1>
            <p className="text-[13px] text-[#b8ac96]">Stripe is finalizing your subscription. This usually takes 5-10 seconds.</p>
            <div className="text-[10px] font-mono text-[#7a6d5a]">Session: {sessionId?.slice(-12)}</div>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  return (
    <div className="min-h-screen bg-[#14100c] text-[#f4ede2] flex items-center justify-center p-8">
      <div className="max-w-md w-full text-center space-y-6" data-testid="payment-cancel-page">
        <div className="w-16 h-16 rounded-full bg-[#241d16] border border-[#7a6d5a] flex items-center justify-center mx-auto">
          <XCircle size={28} className="text-[#7a6d5a]" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#7a6d5a] font-semibold">Payment Cancelled</div>
        <h1 className="font-display text-3xl font-bold">No charge was made.</h1>
        <p className="text-[13px] text-[#b8ac96]">You can subscribe anytime.</p>
        <Link to="/pricing" className="inline-block px-6 py-3 rounded bg-[#d4a574] text-[#14100c] font-semibold">Back to Pricing</Link>
      </div>
    </div>
  );
}
